/**
 * Google Ads gtag Management
 * Loads Google Ads gtag only after marketing consent is granted
 */

import { useEffect } from 'react';
import { hasMarketingConsent } from './cookieConsent';

const GOOGLE_ADS_ID = 'AW-17759067216';
const GTAG_SCRIPT_URL = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;

let gtagLoaded = false;
let scriptElement = null;

/**
 * Initialize dataLayer and gtag function
 */
function initGtag() {
  if (typeof window === 'undefined') return;
  
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function() {
    window.dataLayer.push(arguments);
  };
}

/**
 * Load Google Ads gtag script dynamically
 */
export function loadGoogleAds() {
  if (typeof window === 'undefined') return;
  if (gtagLoaded) return; // Already loaded
  
  // Check consent before loading
  if (!hasMarketingConsent()) {
    return;
  }

  // Initialize dataLayer and gtag function
  initGtag();

  // Load the gtag script
  scriptElement = document.createElement('script');
  scriptElement.async = true;
  scriptElement.src = GTAG_SCRIPT_URL;
  scriptElement.onload = () => {
    gtagLoaded = true;
    // Configure gtag after script loads
    window.gtag('js', new Date());
    window.gtag('config', GOOGLE_ADS_ID);
  };
  document.head.appendChild(scriptElement);
}

/**
 * Unload Google Ads gtag and clear cookies
 */
export function unloadGoogleAds() {
  if (typeof window === 'undefined') return;
  
  // Remove script element
  if (scriptElement && scriptElement.parentNode) {
    scriptElement.parentNode.removeChild(scriptElement);
    scriptElement = null;
  }
  
  // Remove any other gtag script tags
  const scripts = document.querySelectorAll(`script[src*="${GTAG_SCRIPT_URL}"]`);
  scripts.forEach(script => {
    if (script.parentNode) {
      script.parentNode.removeChild(script);
    }
  });

  // Clear Google Ads cookies
  const cookiesToClear = ['_gcl_au', '_gcl_dc', '_gcl_gb', '_gcl_gf', '_gcl_aw'];
  cookiesToClear.forEach(cookieName => {
    // Clear cookie for current domain
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    // Clear cookie for parent domain (if applicable)
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
    // Clear cookie for .domain (if applicable)
    const domainParts = window.location.hostname.split('.');
    if (domainParts.length > 1) {
      const parentDomain = '.' + domainParts.slice(-2).join('.');
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${parentDomain};`;
    }
  });

  // Reset dataLayer (but keep it initialized for potential future use)
  if (window.dataLayer) {
    window.dataLayer.length = 0;
  }

  // Reset gtag function
  if (window.gtag) {
    window.gtag = function() {
      if (window.dataLayer) {
        window.dataLayer.push(arguments);
      }
    };
  }

  gtagLoaded = false;
}

/**
 * React hook to manage Google Ads gtag based on marketing consent
 */
export function useGoogleAds() {
  useEffect(() => {
    // Initial check and load/unload
    const checkAndUpdate = () => {
      const hasConsent = hasMarketingConsent();
      if (hasConsent) {
        loadGoogleAds();
      } else {
        unloadGoogleAds();
      }
    };

    // Initial check
    checkAndUpdate();

    // Listen for storage changes (when consent is updated in another tab/window)
    const handleStorageChange = (e) => {
      if (e.key === 'cookieConsent') {
        checkAndUpdate();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom consent change events
    const handleConsentChange = () => {
      checkAndUpdate();
    };

    window.addEventListener('cookieConsentChanged', handleConsentChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cookieConsentChanged', handleConsentChange);
    };
  }, []);
}

